import { useMutation } from "@apollo/client/react";
import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import ConfirmationModal from "ds/components/ConfirmationModal";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { downloadFile } from "utils/file";

import { RESET_VCS_AGENT_POOL } from "../ItemPage/gql";
import { VcsAgentPoolView } from "../types";

type ResetConfirmationProps = {
  vcsAgentPool: VcsAgentPoolView;
  refetchQueriesOnRefresh?: string[];
  onRefreshSuccess?: () => void;
};

const VcsAgentPoolResetConfirmationModal = create(function VcsAgentPoolResetConfirmationModal({
  vcsAgentPool,
  onRefreshSuccess,
  refetchQueriesOnRefresh,
}: ResetConfirmationProps) {
  const modal = useModal();

  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [resetVCSAgentPool, { loading }] = useMutation(RESET_VCS_AGENT_POOL, {
    awaitRefetchQueries: true,
    refetchQueries: refetchQueriesOnRefresh,
  });

  const onConfirm = useCallback(async () => {
    try {
      const { data } = await resetVCSAgentPool({
        variables: { id: vcsAgentPool.id },
      });

      if (data) {
        const {
          vcsAgentPoolReset: { id, config },
        } = data;

        if (config) {
          downloadFile(`vcs-agent-pool-${id}.config`, config);
        }
        reportSuccess({ message: "VCS agent pool was reset" });
        modal.hide();
        onRefreshSuccess?.();
      }
    } catch (e) {
      onError(e);
    }
  }, [resetVCSAgentPool, onError, reportSuccess, vcsAgentPool.id, modal, onRefreshSuccess]);

  return (
    <ConfirmationModal
      title="Reset VCS agent pool"
      onConfirm={onConfirm}
      isLoading={loading}
      isDismissable={!loading}
    >
      <Typography tag="p" variant="p-body2">
        Resetting this VCS Agent Pool will cause existing agents to no longer be able to connect to
        Spacelift. They will need to be restarted with the new configuration.
      </Typography>
    </ConfirmationModal>
  );
});

export const showVcsAgentPoolResetConfirmation = (props: ResetConfirmationProps) =>
  show(VcsAgentPoolResetConfirmationModal, props);
